
import { pb, TutorProfile, User } from '@/lib/pocketbase';

// Extended type to also support student profiles
type ProfileWithPicture = TutorProfile | User | { 
  profilePicture?: string; 
  profilePictureUrl?: string;
  avatar?: string;
  collectionId?: string;
  collectionName?: string;
  id?: string;
};

export const getProfilePictureUrl = (profile: ProfileWithPicture | null): string => {
  if (!profile) {
    return '';
  }
  
  // Check if it's a TutorProfile with profile picture fields
  if ('profilePictureUrl' in profile) {
    // First check if there's a URL stored and it's a valid URL
    if (profile.profilePictureUrl && profile.profilePictureUrl.trim()) {
      const url = profile.profilePictureUrl.trim();
      // Check if it's a valid URL (starts with http or https)
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
    }
    
    // Then check if there's a file uploaded
    if (profile.profilePicture && profile.profilePicture.trim()) {
      try {
        const fileUrl = pb.files.getURL(profile, profile.profilePicture);
        return fileUrl;
      } catch (error) {
        return '';
      }
    }
  }
  
  // Check if it's a User with avatar field
  if ('avatar' in profile && profile.avatar) {
    try {
      const fileUrl = pb.files.getURL(profile, profile.avatar);
      return fileUrl;
    } catch (error) {
      return '';
    }
  }
  
  return '';
};

export const getUserInitials = (name: string | undefined): string => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

/**
 * Check if user has a profile picture (for both tutors and students)
 * Returns the profile record if it has a picture, null otherwise
 */
export const checkUserProfilePicture = async (userId: string, userRole: string): Promise<any> => {
  try {
    if (userRole === 'tutor') {
      const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
        filter: `user="${userId}"`,
      });
      
      if (tutorProfiles.items.length > 0) {
        const profile = tutorProfiles.items[0];
        const hasPhoto = getProfilePictureUrl(profile);
        return hasPhoto ? profile : null;
      }
    } else if (userRole === 'student') {
      const studentProfiles = await pb.collection('student_profiles').getList(1, 1, {
        filter: `user="${userId}"`,
      });
      
      if (studentProfiles.items.length > 0) {
        const profile = studentProfiles.items[0];
        const hasPhoto = getProfilePictureUrl(profile);
        return hasPhoto ? profile : null;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error checking user profile picture:', error);
    return null;
  }
};
